import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getToken } from '../authentication/authProvider';

import { apiFetchResponse } from '../lib/apiFetch';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

type CrosscheckStatus = 'match' | 'mismatch' | 'review' | 'unreadable';

interface Comparison {
  key: string;
  label: string;
  systemValue: string;
  certificateValue: string;
  status: CrosscheckStatus;
  detail?: string;
}

interface CrosscheckResponse {
  hasCertificate: boolean;
  extraction: { extracted_at: string; model: string; notes: string | null } | null;
  comparisons: Comparison[];
  summary: { match: number; mismatch: number; review: number; unreadable: number } | null;
}

interface MccdCrosscheckProps {
  funeralId: number | string;
  /** funeral_documents.id of the uploaded medical certificate (for the viewer). */
  mccdDocId?: string;
  open: boolean;
  onClose: () => void;
}

const STATUS_STYLE: Record<CrosscheckStatus, { label: string; color: string; bg: string }> = {
  match: { label: 'Match', color: '#1b5e20', bg: '#e8f5e9' },
  mismatch: { label: 'Mismatch', color: '#e65100', bg: '#fff3e0' },
  review: { label: 'Review', color: '#455a64', bg: '#eceff1' },
  unreadable: { label: 'Unreadable', color: '#616161', bg: '#f5f5f5' },
};

const dash = (v: string) => (v && v.trim() ? v : '—');

type TabKey = 'discrepancies' | 'form15' | 'certificate';

/**
 * Warn-only cross-check of the doctor's handwritten Medical Certificate of Death
 * against the values that go onto Form 15. Tabs let staff eyeball the two source
 * documents side by side. The comparison is cached per funeral: reopening shows
 * the last result without re-reading, and a Refresh button re-runs it on demand.
 */
export function MccdCrosscheck({ funeralId, mccdDocId, open, onClose }: MccdCrosscheckProps) {
  const [loading, setLoading] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Persisted across open/close so reopening shows the last compare, not a re-run.
  const [data, setData] = useState<CrosscheckResponse | null>(null);
  const [tab, setTab] = useState<TabKey>('discrepancies');
  const [applyingKey, setApplyingKey] = useState<string | null>(null);

  // Lazily-loaded object URLs for the two document viewers.
  const [form15Url, setForm15Url] = useState<string | null>(null);
  const [certUrl, setCertUrl] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const objectUrls = useRef<string[]>([]);

  const runCompare = useCallback(
    async (recheck: boolean) => {
      recheck ? setRechecking(true) : setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const url = recheck
          ? `${PAPERMAN_API_URL}statement-of-death/mccd-crosscheck/${funeralId}/recheck`
          : `${PAPERMAN_API_URL}statement-of-death/mccd-crosscheck/${funeralId}`;
        const res = await fetch(url, {
          method: recheck ? 'POST' : 'GET',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Cross-check failed (${res.status})`);
        setData((await res.json()) as CrosscheckResponse);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Cross-check failed');
      } finally {
        recheck ? setRechecking(false) : setLoading(false);
      }
    },
    [funeralId],
  );

  // Run once on first open; thereafter show the cached result until Refresh.
  useEffect(() => {
    if (open && !data && !loading && !error) void runCompare(false);
  }, [open, data, loading, error, runCompare]);

  // Replace our value with the certificate's, updating the system record.
  const applyReplace = useCallback(
    async (key: string) => {
      setApplyingKey(key);
      setError(null);
      try {
        const token = await getToken();
        const res = await fetch(`${PAPERMAN_API_URL}statement-of-death/mccd-crosscheck/${funeralId}/apply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ key }),
        });
        if (!res.ok) throw new Error(`Replace failed (${res.status})`);
        setData((await res.json()) as CrosscheckResponse);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Replace failed');
      } finally {
        setApplyingKey(null);
      }
    },
    [funeralId],
  );

  // Fetch a document blob for a viewer tab the first time it's shown.
  const loadDoc = useCallback(
    async (which: 'form15' | 'certificate') => {
      setDocError(null);
      try {
        const path =
          which === 'form15'
            ? `statement-of-death/form/${funeralId}`
            : `documents/funeral-document/${mccdDocId}/download`;
        const res = await apiFetchResponse(path);
        if (!res.ok) throw new Error(`Failed to load document (${res.status})`);
        const blobUrl = URL.createObjectURL(await res.blob());
        objectUrls.current.push(blobUrl);
        which === 'form15' ? setForm15Url(blobUrl) : setCertUrl(blobUrl);
      } catch (e) {
        setDocError(e instanceof Error ? e.message : 'Failed to load document');
      }
    },
    [funeralId, mccdDocId],
  );

  // Preload both document viewers as soon as the modal opens, so switching tabs
  // is instant.
  useEffect(() => {
    if (!open) return;
    if (!form15Url) void loadDoc('form15');
    if (mccdDocId && !certUrl) void loadDoc('certificate');
  }, [open, form15Url, certUrl, mccdDocId, loadDoc]);

  // Revoke object URLs on unmount.
  useEffect(() => () => objectUrls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const summary = data?.summary;
  const flagged = summary ? summary.mismatch + summary.review + summary.unreadable : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { height: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
        Medical Certificate cross-check
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
          {summary && (
            <Chip
              size="small"
              label={flagged === 0 ? 'No discrepancies' : `${flagged} to review`}
              sx={{
                backgroundColor: flagged === 0 ? '#e8f5e9' : '#fff3e0',
                color: flagged === 0 ? '#1b5e20' : '#e65100',
                fontWeight: 600,
              }}
            />
          )}
          {data?.extraction && (
            <Typography variant="caption" color="text.secondary">
              Read {new Date(data.extraction.extracted_at).toLocaleString()} · {data.extraction.model}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            onClick={() => void runCompare(true)}
            disabled={loading || rechecking}
            startIcon={rechecking ? <CircularProgress size={14} /> : <RefreshIcon />}
          >
            {rechecking ? 'Re-reading…' : 'Refresh'}
          </Button>
        </Box>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="discrepancies" label="Discrepancies" />
        <Tab value="form15" label="Form 15" />
        <Tab value="certificate" label="Medical Certificate" />
      </Tabs>

      <DialogContent sx={{ flexGrow: 1, p: tab === 'discrepancies' ? 2 : 0, overflow: 'hidden' }}>
        {tab === 'discrepancies' && (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {!loading && error && (
              <Typography color="error" sx={{ py: 2 }}>
                {error}
              </Typography>
            )}
            {!loading && !error && data && !data.hasCertificate && (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No Medical Certificate uploaded yet. Upload it in the Documents tab to enable the cross-check.
              </Typography>
            )}
            {!loading && !error && data?.hasCertificate && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Our value (Form 15)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Certificate</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.comparisons.map((c) => {
                    const style = STATUS_STYLE[c.status];
                    return (
                      <TableRow key={c.key}>
                        <TableCell sx={{ verticalAlign: 'top', fontWeight: 500 }}>{c.label}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>{dash(c.systemValue)}</TableCell>
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            whiteSpace: 'pre-wrap',
                            backgroundColor: c.status === 'match' ? 'transparent' : style.bg,
                          }}
                        >
                          {dash(c.certificateValue)}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Chip
                            size="small"
                            label={style.label}
                            sx={{ backgroundColor: style.bg, color: style.color, fontWeight: 600 }}
                          />
                          {c.detail && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {c.detail}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                          {c.status !== 'match' && c.certificateValue?.trim() && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={applyingKey !== null}
                              onClick={() => void applyReplace(c.key)}
                              startIcon={applyingKey === c.key ? <CircularProgress size={12} /> : undefined}
                              title="Replace our value with the certificate's and update the record"
                            >
                              Replace
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {tab === 'form15' && (
          <DocFrame url={form15Url} error={docError} title="Form 15" />
        )}
        {tab === 'certificate' && (
          !mccdDocId ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">No Medical Certificate uploaded.</Typography>
            </Box>
          ) : (
            <DocFrame url={certUrl} error={docError} title="Medical Certificate" />
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

function DocFrame({ url, error, title }: { url: string | null; error: string | null; title: string }) {
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (!url) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  return <iframe title={title} src={url} style={{ width: '100%', height: '100%', border: 0 }} />;
}

export default MccdCrosscheck;
